import { useMemo } from 'react';
import { useListPatientBalancesQuery } from '@frontend/api-collections';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../../../store/bulk-collections-filter-store';
import { mapPatientBalance } from '../../../mapper';
import { getFilterString } from '../../../utils';

export const DEFAULT_PAGE_SIZE = 20;

type UseBaseQueryParams = {
  enabled: boolean;
  pageToken?: string;
  pageSize?: number;
  orderBy?: string;
  keepPreviousData?: boolean;
};

export const useBulkCollectionsQueryBase = ({
  enabled,
  pageToken,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy,
  keepPreviousData = false,
}: UseBaseQueryParams) => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();
  const { balanceType, searchValue } = useBulkCollectionsFilterShallowStore('balanceType', 'searchValue');

  const { data: rawData, isFetching } = useListPatientBalancesQuery({
    request: {
      locationIds: selectedLocationIds,
      pageToken,
      pageSize,
      orderBy,
      filter: getFilterString(balanceType, searchValue),
    },
    options: {
      enabled: enabled && selectedLocationIds.length > 0,
      keepPreviousData,
      onError: () => {
        alerts.error(t('Error fetching collections'));
      },
    },
  });

  const data = useMemo(() => (rawData?.patientBalances ?? []).map(mapPatientBalance), [rawData?.patientBalances]);
  const nextPageToken = rawData?.nextPageToken;

  return {
    data,
    isFetching,
    nextPageToken,
  };
};
